import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';

import type { ForecastReviewBulkActionResponse } from '@/services/forecastReviewService';

export type BulkRejectModalPhase = 'confirm' | 'applying' | 'done' | 'error';

interface BulkRejectModalProps {
  open: boolean;
  alertCount: number;
  phase: BulkRejectModalPhase;
  result: ForecastReviewBulkActionResponse | null;
  errorMessage: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function BulkRejectModal({
  open,
  alertCount,
  phase,
  result,
  errorMessage,
  onConfirm,
  onClose,
}: BulkRejectModalProps) {
  const { t } = useTranslation();

  if (!open) return null;

  const isBusy = phase === 'applying';
  const canClose = !isBusy;

  const content = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={canClose ? onClose : undefined}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-reject-modal-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="bulk-reject-modal-title" className="text-base font-semibold text-gray-900">
              {phase === 'done'
                ? t('forecastReview.alerts.bulkRejectModal.doneTitle')
                : t('forecastReview.alerts.bulkRejectModal.title')}
            </h2>
            {alertCount > 0 ? (
              <p className="mt-1 text-sm text-gray-600">
                {t('forecastReview.alerts.bulkRejectModal.scope', { count: alertCount })}
              </p>
            ) : null}
          </div>
          {canClose ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('forecastReview.alerts.bulkRejectModal.close')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="px-5 py-4">
          {phase === 'applying' ? (
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-stocker-primary" />
              <span>{t('forecastReview.alerts.bulkRejectModal.applying')}</span>
            </div>
          ) : phase === 'done' && result ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t('forecastReview.alerts.bulkRejectModal.success', {
                    succeeded: result.succeeded_count,
                    requested: result.requested_count,
                  })}
                </span>
              </div>
              {result.failed_count > 0 ? (
                <p className="text-amber-800">
                  {t('forecastReview.alerts.bulkRejectModal.partialFailures', {
                    failed: result.failed_count,
                  })}
                </p>
              ) : null}
            </div>
          ) : phase === 'error' ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMessage ?? t('forecastReview.alerts.bulkRejectModal.errorGeneric')}</span>
            </div>
          ) : alertCount > 0 ? (
            <p className="text-sm text-gray-600">{t('forecastReview.alerts.bulkRejectModal.message')}</p>
          ) : (
            <p className="text-sm text-gray-600">{t('forecastReview.alerts.bulkRejectModal.empty')}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          {phase === 'confirm' ? (
            <>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onClose}
                disabled={isBusy}
              >
                {t('forecastReview.alerts.bulkRejectModal.cancel')}
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                onClick={() => void onConfirm()}
                disabled={isBusy || alertCount === 0}
              >
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('forecastReview.alerts.bulkRejectModal.confirm')}
              </button>
            </>
          ) : phase === 'done' || phase === 'error' ? (
            <button
              type="button"
              className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              onClick={onClose}
            >
              {t('forecastReview.alerts.bulkRejectModal.close')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
